import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Badge,
  Button,
  FileButton,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import qrcode from 'qrcode-generator';
import { useParams } from 'react-router';
import type { z } from 'zod';
import type { trainingDetailSchema, trainingOptionsSchema } from '@isg/validation';
import {
  useCiktiIndir,
  useEgitim,
  useEgitimEkSil,
  useEgitimEkYukle,
  useEgitimEylem,
  useEgitimOptions,
  useEgitimQrUret,
} from '../api/queries';
import { EGITIM_SONUC_ETIKET, EGITIM_TARAF_ETIKET, EGITIM_YONTEM_ETIKET } from '../i18n/tr';
import { Bolum, KapiSebebi, KayitDetayi, Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { KatilimciSecici } from './egitim-parts';

type EgitimDetay = z.output<typeof trainingDetailSchema>;
type EgitimOptions = z.output<typeof trainingOptionsSchema>;

/**
 * Eğitim detayı (F3B·S7 — Kapsam §16.10; ✏️ tur 5 / Y2 — sayfa sözleşmesine
 * hizalandı: `KayitDetayi` + `Bolum`, sekme yok, **modal yok**).
 *
 * Y2'nin dört kalemi:
 *   K3 — katılımcı EKLEME satır içi soluk satır + toplu seçim (P1-a); eski
 *        akış katılımcıyı yalnız kayıt açılırken alıyordu.
 *   K4 — künye düzeltme aynı sayfada (tür/işyeri DEĞİŞMEZ — kaydın kimliği).
 *   K5 — konu düzeltme (temel İSG müfredat işaretleri; TAM liste gönderilir).
 *   K16 — sonuç işaretleme satır İÇİNDE (modal yalnız onay sorusudur — P1);
 *        QR üretimi de kendi bölümünde.
 *
 * Düğme görünürlüğü SUNUCUDAN (`yetkiler`): ekran ikinci kural yazmaz.
 * Üç şey TÜRETİLMİŞTİR ve burada yeniden hesaplanmaz: kişi×saat · yanıt oranı
 * · süre uyarıları. Sınav ÖNERİSİ sunucudan gelir ve sonucu YAZMAZ (T3).
 * ⚠️ M1: açıklama cümlesi eklenmedi; mevcut cümleler envanterde.
 */
export function EgitimDetayScreen(): React.JSX.Element {
  const { trainingId } = useParams();
  const egitim = useEgitim(trainingId ?? null);
  const options = useEgitimOptions();
  const indir = useCiktiIndir();

  return (
    <>
      <QueryStatus queries={[egitim]} />
      {egitim.isSuccess && (
        <KayitDetayi
          geriYol="/egitimler"
          geriEtiket="Eğitimler"
          baslik={egitim.data.egitimTuruAd}
          testId="egitim-detay"
          ustBilgi={
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {tarihGoster(egitim.data.tarih)} · {egitim.data.workplaceAd}
                {egitim.data.locationAd ? ` — ${egitim.data.locationAd}` : ''}
              </Text>
              {/* SÖ-1 — sistem öncesi kayıt rozeti (ekipman kontrol geçmişi emsali, anlam `bilgi`). */}
              {egitim.data.devirMi && (
                <Rozet anlam="bilgi" testId="egitim-devir-rozeti">
                  Sistem öncesi kayıt
                </Rozet>
              )}
            </Group>
          }
          eylemler={
            <Group>
              {/* Y3 — ıslak imza döngüsünün kâğıdı (S9 motoru): kaydı görebilen alır. */}
              <Button
                variant="default"
                data-testid="egitim-katilim-formu-pdf"
                loading={indir.isPending}
                onClick={() =>
                  indir.mutate({
                    yol: `/api/trainings/${egitim.data.id}/katilim-formu.pdf`,
                    yedekAd: 'egitim-katilim-formu.pdf',
                  })
                }
              >
                Katılım formu (PDF)
              </Button>
            </Group>
          }
        >
          <Kunye kayit={egitim.data} options={options.data} />
          {egitim.data.uyarilar.length > 0 && (
            <Alert color={ANLAM.dikkat} role="status" data-testid="egitim-uyari-bandi">
              <Stack gap={2}>
                {egitim.data.uyarilar.map((uyari) => (
                  <Text key={uyari} size="sm">
                    {uyari}
                  </Text>
                ))}
                <Text size="xs" c="dimmed">
                  Bu uyarılar kaydı engellemez — sistem hesaplar ve önerir, karar sahadadır.
                </Text>
              </Stack>
            </Alert>
          )}
          <Katilimcilar kayit={egitim.data} options={options.data} />
          <Konular kayit={egitim.data} options={options.data} />
          <Ekler kayit={egitim.data} />
          {egitim.data.yetkiler.qrUretebilir && (
            <QrBolumu id={egitim.data.id} ad={egitim.data.egitimTuruAd} />
          )}
        </KayitDetayi>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// KÜNYE — okuma ızgarası (P9) + K4 düzeltme (tür/işyeri DEĞİŞMEZ)
// ═══════════════════════════════════════════════════════════════════════════

function Kunye({
  kayit,
  options,
}: {
  kayit: EgitimDetay;
  options: EgitimOptions | undefined;
}): React.JSX.Element {
  const eylem = useEgitimEylem();
  const [duzenleme, setDuzenleme] = useState(false);
  const disEgitici = kayit.egiticiPersonId === null;
  const [tarih, setTarih] = useState(kayit.tarih);
  // SÖ-1: devir kaydında süre/yöntem boş olabilir — düzenleme boş başlar, boş kaydedilmez
  // (form şeması süreyi ve yöntemi ister; devir kaydının NULL'u düzenlenmeden korunur).
  const [sure, setSure] = useState<number | string>(kayit.sureDersSaati ?? '');
  const [yontem, setYontem] = useState<string>(kayit.yontem ?? 'yuz_yuze');
  const [egiticiAd, setEgiticiAd] = useState(disEgitici ? (kayit.egiticiAd ?? '') : '');
  const [egiticiKurum, setEgiticiKurum] = useState(kayit.egiticiKurum ?? '');
  const [egiticiKimlik, setEgiticiKimlik] = useState('');
  const [egiticiPersonId, setEgiticiPersonId] = useState<string | null>(kayit.egiticiPersonId);
  const [sorumlu, setSorumlu] = useState<string | null>(kayit.sorumluPersonId);
  const [egitimYeri, setEgitimYeri] = useState<string | null>(kayit.egitimYeriRefId);
  const [sertifikaTipi, setSertifikaTipi] = useState<string | null>(kayit.sertifikaTipiRefId);
  const [notlar, setNotlar] = useState(kayit.notlar ?? '');

  const alanlar: [string, string, string][] = [
    ['Tarih', tarihGoster(kayit.tarih), 'egitim-kunye-tarih'],
    [
      'Süre',
      kayit.sureDersSaati === null ? '—' : `${kayit.sureDersSaati} ders saati`,
      'egitim-kunye-sure',
    ],
    [
      'Yöntem',
      kayit.yontem === null ? '—' : (EGITIM_YONTEM_ETIKET[kayit.yontem] ?? kayit.yontem),
      'egitim-kunye-yontem',
    ],
    // Eğitici İKİ ALAN (E1): kurum ayrı durur, tek satıra gömülmez.
    ['Eğitici', kayit.egiticiAd ?? '—', 'egitim-kunye-egitici'],
    ['Eğitici kurumu', kayit.egiticiKurum ?? '—', 'egitim-kunye-kurum'],
    // İ4 — TC maskeli iner (madde 37 kuralı).
    ['Eğitici TC', kayit.egiticiKimlikNoMaskeli ?? '—', 'egitim-kunye-egitici-tc'],
    ['Sorumlu İSG profesyoneli', kayit.sorumluAd ?? '—', 'egitim-kunye-sorumlu'],
    ['Eğitim yeri', kayit.egitimYeriAd ?? '—', 'egitim-kunye-yer'],
    ['Sertifika tipi', kayit.sertifikaTipiAd ?? '—', 'egitim-kunye-sertifika'],
    ['Kişi × saat', `${kayit.kisiSaat} (katılmayanlar sayılmaz)`, 'egitim-kisi-saat'],
    [
      'Değerlendirme',
      `${kayit.katilimciSayisi} katılımcı, ${kayit.degerlendirmeYanitSayisi} yanıt`,
      'egitim-kunye-degerlendirme',
    ],
  ];

  return (
    <Bolum baslik="Künye" testId="egitim-kunye">
      {!duzenleme ? (
        <Stack gap="xs">
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            {alanlar.map(([etiket, deger, testId]) => (
              <Stack key={etiket} gap={0} data-testid={testId}>
                <Text size="xs" c="dimmed">
                  {etiket}
                </Text>
                <Text size="sm">{deger}</Text>
              </Stack>
            ))}
          </SimpleGrid>
          {kayit.notlar && <Text size="sm">{kayit.notlar}</Text>}
          {kayit.yetkiler.duzenleyebilir && (
            <Group>
              <Button
                size="xs"
                variant="default"
                data-testid="egitim-kunye-duzenle"
                onClick={() => {
                  eylem.reset();
                  setDuzenleme(true);
                }}
              >
                Künyeyi düzenle
              </Button>
            </Group>
          )}
        </Stack>
      ) : (
        <Stack gap="sm" data-testid="egitim-kunye-duzenleme">
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            {/* C2 (5½) — takvim seçici + `gunGirdisiIso` (madde 137 deseni):
                geçersiz tarihte kaydet kapalı, sebebi sunucudan değil ekrandan. */}
            <TextInput
              label="Tarih"
              type="date"
              data-testid="egitim-kunye-tarih-giris"
              value={tarih}
              onChange={(e) => setTarih(e.currentTarget.value)}
              error={gunGirdisiIso(tarih) === null ? 'Geçerli bir gün seçin' : undefined}
            />
            <NumberInput
              label="Süre (ders saati)"
              min={0.5}
              step={0.5}
              decimalScale={1}
              data-testid="egitim-kunye-sure-giris"
              value={sure}
              onChange={setSure}
            />
            <Select
              label="Yöntem"
              allowDeselect={false}
              data-testid="egitim-kunye-yontem-giris"
              data={Object.entries(EGITIM_YONTEM_ETIKET).map(([value, label]) => ({
                value,
                label,
              }))}
              value={yontem}
              onChange={(d) => setYontem(d ?? 'yuz_yuze')}
            />
            {disEgitici ? (
              <>
                <TextInput
                  label="Eğitici adı"
                  data-testid="egitim-kunye-egitici-giris"
                  value={egiticiAd}
                  onChange={(e) => setEgiticiAd(e.currentTarget.value)}
                />
                <TextInput
                  label="Eğitici kurumu"
                  data-testid="egitim-kunye-kurum-giris"
                  value={egiticiKurum}
                  onChange={(e) => setEgiticiKurum(e.currentTarget.value)}
                />
                <TextInput
                  label={
                    kayit.egiticiKimlikNoMaskeli
                      ? `Eğitici TC (kayıtlı: ${kayit.egiticiKimlikNoMaskeli})`
                      : 'Eğitici TC (isteğe bağlı)'
                  }
                  placeholder="değiştirmek için yazın"
                  maxLength={11}
                  data-testid="egitim-kunye-egitici-tc-giris"
                  value={egiticiKimlik}
                  onChange={(e) => setEgiticiKimlik(e.currentTarget.value)}
                />
              </>
            ) : (
              <Select
                label="İç eğitici"
                searchable
                data-testid="egitim-kunye-egitici-person-giris"
                data={(options?.personeller ?? []).map((p) => ({ value: p.id, label: p.adSoyad }))}
                value={egiticiPersonId}
                onChange={setEgiticiPersonId}
              />
            )}
            <Select
              label="Sorumlu İSG profesyoneli"
              searchable
              clearable
              data-testid="egitim-kunye-sorumlu-giris"
              data={(options?.personeller ?? []).map((p) => ({ value: p.id, label: p.adSoyad }))}
              value={sorumlu}
              onChange={setSorumlu}
            />
            {(options?.egitimYerleri.length ?? 0) > 0 && (
              <Select
                label="Eğitim yeri"
                clearable
                data-testid="egitim-kunye-yer-giris"
                data={(options?.egitimYerleri ?? []).map((k) => ({ value: k.id, label: k.ad }))}
                value={egitimYeri}
                onChange={setEgitimYeri}
              />
            )}
            {(options?.sertifikaTipleri.length ?? 0) > 0 && (
              <Select
                label="Sertifika tipi"
                clearable
                data-testid="egitim-kunye-sertifika-giris"
                data={(options?.sertifikaTipleri ?? []).map((k) => ({ value: k.id, label: k.ad }))}
                value={sertifikaTipi}
                onChange={setSertifikaTipi}
              />
            )}
          </SimpleGrid>
          <Textarea
            label="Notlar"
            data-testid="egitim-kunye-notlar-giris"
            value={notlar}
            onChange={(e) => setNotlar(e.currentTarget.value)}
          />
          {eylem.isError && (
            <Alert color={ANLAM.engel} role="alert" data-testid="egitim-kunye-hata">
              {eylem.error.message}
            </Alert>
          )}
          <Group>
            <Button
              size="xs"
              data-testid="egitim-kunye-kaydet"
              disabled={gunGirdisiIso(tarih) === null}
              loading={eylem.isPending}
              onClick={() =>
                eylem.mutate(
                  {
                    id: kayit.id,
                    yol: '',
                    metot: 'patch',
                    govde: {
                      tarih,
                      sureDersSaati: Number(sure),
                      yontem,
                      ...(disEgitici
                        ? {
                            egiticiAd: egiticiAd.trim() || null,
                            egiticiKurum: egiticiKurum.trim() || null,
                            ...(egiticiKimlik.trim() !== ''
                              ? { egiticiKimlikNo: egiticiKimlik.trim() }
                              : {}),
                          }
                        : { egiticiPersonId }),
                      sorumluPersonId: sorumlu,
                      egitimYeriRefId: egitimYeri,
                      sertifikaTipiRefId: sertifikaTipi,
                      notlar: notlar.trim() === '' ? null : notlar.trim(),
                    },
                  },
                  { onSuccess: () => setDuzenleme(false) },
                )
              }
            >
              Kaydet
            </Button>
            <Button
              size="xs"
              variant="subtle"
              data-testid="egitim-kunye-vazgec"
              onClick={() => setDuzenleme(false)}
            >
              Vazgeç
            </Button>
          </Group>
        </Stack>
      )}
    </Bolum>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// KATILIMCILAR — K3 soluk satır (toplu) · K16 satır içi sonuç · P2 eylemler
// ═══════════════════════════════════════════════════════════════════════════

function Katilimcilar({
  kayit,
  options,
}: {
  kayit: EgitimDetay;
  options: EgitimOptions | undefined;
}): React.JSX.Element {
  const eylem = useEgitimEylem();
  const indir = useCiktiIndir();
  const [acikSatir, setAcikSatir] = useState<string | null>(null);
  const [secilenler, setSecilenler] = useState<string[]>([]);
  // P12 (madde 224) — kaydedilmemiş katılımcı seçimi çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(secilenler.length > 0 ? 1 : 0, 'katılımcı seçimi');
  const yazabilir = kayit.yetkiler.duzenleyebilir;
  const mevcut = new Set(kayit.katilimcilar.map((k) => k.personId));
  const yukleniciler = new Set(options?.yukleniciPersonIds ?? []);
  const adaylar = (options?.personeller ?? []).filter((p) => !mevcut.has(p.id));
  const KOLON = 6;

  return (
    <Bolum baslik="Katılımcılar" testId="egitim-katilimcilar">
      <Table data-testid="egitim-katilimci-tablosu">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Kişi</Table.Th>
            <Table.Th>Taraf</Table.Th>
            <Table.Th>Sonuç</Table.Th>
            <Table.Th>Ön / son test</Table.Th>
            <Table.Th>Sınav denemesi</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {kayit.katilimcilar.map((k) =>
            acikSatir === k.id ? (
              <SonucSatiri
                key={k.id}
                kayitId={kayit.id}
                satir={k}
                kapat={() => setAcikSatir(null)}
              />
            ) : (
              <Table.Tr key={k.id} data-testid={`egitim-katilimci-${k.id}`}>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{k.adSoyad}</Text>
                    {k.yetkinlikDogdu && (
                      <Badge color={ANLAM.olumlu} size="sm" data-testid="egitim-yetkinlik-dogdu">
                        yetkinlik
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{EGITIM_TARAF_ETIKET[k.taraf] ?? k.taraf}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">{EGITIM_SONUC_ETIKET[k.sonuc] ?? k.sonuc}</Text>
                    {/* ÖNERİ — kayda YAZILMAZ (T3'ün özü). */}
                    {k.onerilenSonuc && k.onerilenSonuc !== k.sonuc && (
                      <Badge color={ANLAM.bilgi} size="sm" data-testid="egitim-sinav-onerisi">
                        öneri: {EGITIM_SONUC_ETIKET[k.onerilenSonuc]}
                      </Badge>
                    )}
                    {k.bastanAlinmali && (
                      <Badge color={ANLAM.dikkat} size="sm" data-testid="egitim-bastan-alinmali">
                        baştan alınmalı
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {k.onTestSkoru ?? '—'} / {k.sonTestSkoru ?? '—'}
                </Table.Td>
                <Table.Td>{k.sinavDeneme}</Table.Td>
                <Table.Td>
                  <SatirEylemleri
                    testId={`egitim-katilimci-eylemleri-${k.id}`}
                    birincil={
                      yazabilir
                        ? {
                            etiket: 'Sonuç işaretle',
                            testId: `egitim-sonuc-ac-${k.id}`,
                            onClick: () => {
                              eylem.reset();
                              setAcikSatir(k.id);
                            },
                          }
                        : undefined
                    }
                    digerleri={[
                      {
                        // Y3 — T10 tamamlama belgesi: yalnız başarılı satırda.
                        etiket: 'Tamamlama belgesi (PDF)',
                        gizli: k.sonuc !== 'basarili',
                        testId: `egitim-belge-pdf-${k.id}`,
                        onClick: () =>
                          indir.mutate({
                            yol: `/api/trainings/${kayit.id}/participants/${k.id}/belge.pdf`,
                            yedekAd: 'egitim-belgesi.pdf',
                          }),
                      },
                      {
                        etiket: 'Çıkar',
                        tehlikeli: true,
                        gizli: !yazabilir,
                        testId: `egitim-katilimci-sil-${k.id}`,
                        onClick: () =>
                          eylem.mutate({
                            id: kayit.id,
                            yol: `participants/${k.id}`,
                            metot: 'delete',
                          }),
                      },
                    ]}
                  />
                </Table.Td>
              </Table.Tr>
            ),
          )}
          {/* K3 — SOLUK SATIR (P1-a): katılımcı satır içi eklenir, "Ekle"
              düğmesi yoktur; toplu seçim (salonda liste tek seferde işlenir).
              Taraf istihdamdan OTOMATİK (T2). */}
          {yazabilir && (
            <SolukSatir
              testId="egitim-katilimci-soluk-satiri"
              dokunuldu={secilenler.length > 0}
              kaydedilebilir={secilenler.length > 0}
              kaydediliyor={eylem.isPending}
              kolonSayisi={KOLON}
              onKaydet={() =>
                eylem.mutate(
                  {
                    id: kayit.id,
                    yol: 'participants',
                    govde: {
                      katilimcilar: secilenler.map((personId) => ({
                        personId,
                        taraf: yukleniciler.has(personId) ? 'yuklenici' : 'ana_firma',
                      })),
                    },
                  },
                  { onSuccess: () => setSecilenler([]) },
                )
              }
            >
              <Table.Td colSpan={KOLON - 1}>
                {/* C3 (5½) — kayıt formuyla TEK desen (`egitim-parts.tsx`). */}
                <KatilimciSecici
                  adaylar={adaylar}
                  value={secilenler}
                  onChange={setSecilenler}
                  testId="egitim-katilimci-sec"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {/* P13 — `egitim_kaydi` listesi → bahçe; liste SUNUCUDAN (tenant ayarı). */}
      <KapiSebebi
        durum={kayit.yetkiler.yazmaKapisi}
        nesne="Katılımcıyı"
        fiil="ekler"
        testId="egitim-katilimci-yazamama-sebebi"
      />
      {eylem.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="egitim-katilimci-hata">
          {eylem.error.message}
        </Text>
      )}
    </Bolum>
  );
}

/** K16 — SONUÇ SATIR İÇİNDE (modal yalnız onay sorusudur). Baraj ÖNERİR, yazmaz. */
function SonucSatiri({
  kayitId,
  satir,
  kapat,
}: {
  kayitId: string;
  satir: EgitimDetay['katilimcilar'][number];
  kapat: () => void;
}): React.JSX.Element {
  const eylem = useEgitimEylem();
  const [sonuc, setSonuc] = useState<string>(satir.sonuc);
  const [onTest, setOnTest] = useState<number | string>(satir.onTestSkoru ?? '');
  const [sonTest, setSonTest] = useState<number | string>(satir.sonTestSkoru ?? '');
  const [yeniDeneme, setYeniDeneme] = useState(false);
  return (
    <Table.Tr data-testid={`egitim-sonuc-satiri-${satir.id}`}>
      <Table.Td>
        <Text size="sm">{satir.adSoyad}</Text>
      </Table.Td>
      <Table.Td>{EGITIM_TARAF_ETIKET[satir.taraf] ?? satir.taraf}</Table.Td>
      <Table.Td>
        <Select
          size="xs"
          allowDeselect={false}
          aria-label="Sonuç"
          data-testid="egitim-sonuc-secim"
          comboboxProps={{ withinPortal: true }}
          data={Object.entries(EGITIM_SONUC_ETIKET).map(([value, label]) => ({ value, label }))}
          value={sonuc}
          onChange={(d) => setSonuc(d ?? satir.sonuc)}
        />
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <NumberInput
            size="xs"
            w={80}
            min={0}
            max={100}
            aria-label="Ön test"
            data-testid="egitim-on-test"
            value={onTest}
            onChange={setOnTest}
          />
          <NumberInput
            size="xs"
            w={80}
            min={0}
            max={100}
            aria-label="Son test"
            data-testid="egitim-son-test"
            value={sonTest}
            onChange={setSonTest}
          />
        </Group>
      </Table.Td>
      <Table.Td>
        <Switch
          size="xs"
          label={`Yeni deneme (${satir.sinavDeneme} → ${satir.sinavDeneme + 1})`}
          data-testid="egitim-yeni-deneme"
          checked={yeniDeneme}
          onChange={(e) => setYeniDeneme(e.currentTarget.checked)}
        />
      </Table.Td>
      <Table.Td>
        <Stack gap={4}>
          <Group gap={4} wrap="nowrap">
            <Button
              size="compact-xs"
              data-testid="egitim-sonuc-kaydet"
              loading={eylem.isPending}
              onClick={() =>
                eylem.mutate(
                  {
                    id: kayitId,
                    yol: `participants/${satir.id}/sonuc`,
                    govde: {
                      sonuc,
                      ...(onTest === '' ? {} : { onTestSkoru: Number(onTest) }),
                      ...(sonTest === '' ? {} : { sonTestSkoru: Number(sonTest) }),
                      yeniDeneme,
                    },
                  },
                  { onSuccess: kapat },
                )
              }
            >
              Kaydet
            </Button>
            <Button
              size="compact-xs"
              variant="subtle"
              data-testid="egitim-sonuc-vazgec"
              onClick={kapat}
            >
              Vazgeç
            </Button>
          </Group>
          {eylem.isError && (
            <Text size="xs" c={ANLAM.engel} role="alert" data-testid="egitim-sonuc-hata">
              {eylem.error.message}
            </Text>
          )}
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// KONULAR — K5 düzeltme (yalnız ders saati kuralı taşıyan türde)
// ═══════════════════════════════════════════════════════════════════════════

function Konular({
  kayit,
  options,
}: {
  kayit: EgitimDetay;
  options: EgitimOptions | undefined;
}): React.JSX.Element | null {
  const eylem = useEgitimEylem();
  const [duzenleme, setDuzenleme] = useState(false);
  const [secim, setSecim] = useState<string[]>(kayit.konular.map((k) => k.konuRefId));
  const tur = options?.egitimTurleri.find((t) => t.id === kayit.egitimTuruRefId);
  // Müfredat konusu yalnız ders saati kuralı taşıyan türde sorulur (EK.1) —
  // koşul SUNUCUDAN (`konuIster`); konusuz ve konu istemeyen tür bölüm çizmez.
  if (kayit.konular.length === 0 && !(tur?.konuIster && kayit.yetkiler.duzenleyebilir)) return null;
  return (
    <Bolum baslik="İşlenen konular" testId="egitim-konular-karti">
      {!duzenleme ? (
        <Stack gap="xs">
          <Group gap="xs">
            {kayit.konular.map((k) => (
              <Badge key={k.id} variant="light" data-testid="egitim-konu-rozeti">
                {k.ad}
              </Badge>
            ))}
            {kayit.konular.length === 0 && (
              <Text size="xs" c="dimmed">
                Konu işaretlenmemiş
              </Text>
            )}
          </Group>
          {kayit.yetkiler.duzenleyebilir && tur?.konuIster && (
            <Group>
              <Button
                size="xs"
                variant="default"
                data-testid="egitim-konu-duzenle"
                onClick={() => {
                  eylem.reset();
                  setSecim(kayit.konular.map((k) => k.konuRefId));
                  setDuzenleme(true);
                }}
              >
                Konuları düzenle
              </Button>
            </Group>
          )}
        </Stack>
      ) : (
        <Stack gap="xs" data-testid="egitim-konu-duzenleme">
          <MultiSelect
            searchable
            aria-label="İşlenen konular"
            data-testid="egitim-konu-sec"
            comboboxProps={{ withinPortal: true }}
            data={(options?.egitimKonulari ?? []).map((k) => ({
              value: k.id,
              label: k.grup ? `${k.ad} (${k.grup})` : k.ad,
            }))}
            value={secim}
            onChange={setSecim}
          />
          {eylem.isError && (
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="egitim-konu-hata">
              {eylem.error.message}
            </Text>
          )}
          <Group>
            <Button
              size="xs"
              data-testid="egitim-konu-kaydet"
              loading={eylem.isPending}
              // Gönderilen liste kaydın TAM listesidir (eksik düşer, yeni eklenir).
              onClick={() =>
                eylem.mutate(
                  { id: kayit.id, yol: 'konular', govde: { konuRefIds: secim } },
                  { onSuccess: () => setDuzenleme(false) },
                )
              }
            >
              Kaydet
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setDuzenleme(false)}>
              Vazgeç
            </Button>
          </Group>
        </Stack>
      )}
    </Bolum>
  );
}

function Ekler({ kayit }: { kayit: EgitimDetay }): React.JSX.Element {
  const yukle = useEgitimEkYukle();
  const sil = useEgitimEkSil();
  return (
    <Bolum baslik="Ekler (imzalı katılım formu taraması)" testId="egitim-ekler">
      <Stack gap="sm">
        {kayit.yetkiler.duzenleyebilir && (
          <Group>
            <FileButton onChange={(dosya) => dosya && yukle.mutate({ id: kayit.id, dosya })}>
              {(props) => (
                <Button {...props} size="xs" variant="light" data-testid="egitim-ek-yukle">
                  Dosya yükle
                </Button>
              )}
            </FileButton>
          </Group>
        )}
        <KapiSebebi
          durum={kayit.yetkiler.yazmaKapisi}
          nesne="Eki"
          fiil="yükler"
          testId="egitim-ek-yazamama-sebebi"
        />
        {kayit.ekler.length === 0 && (
          <Text size="sm" c="dimmed">
            Ek yok. Sistem çıktısı imzalanıp taranarak buraya yüklenir.
          </Text>
        )}
        {kayit.ekler.map((ek) => (
          <Group key={ek.id} justify="space-between" data-testid={`egitim-ek-${ek.id}`}>
            <Text size="sm">
              {ek.dosyaAdi} · {(ek.boyutBytes / 1024).toFixed(0)} KB
            </Text>
            {kayit.yetkiler.duzenleyebilir && (
              <Button
                size="xs"
                variant="subtle"
                color={ANLAM.engel}
                onClick={() => sil.mutate({ id: kayit.id, ekId: ek.id })}
              >
                Sil
              </Button>
            )}
          </Group>
        ))}
        {(yukle.isError || sil.isError) && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="egitim-ek-hata">
            {(yukle.error ?? sil.error)?.message}
          </Text>
        )}
      </Stack>
    </Bolum>
  );
}

/**
 * DEĞERLENDİRME QR'I (§16.10/9): kare EĞİTİMİ tanır, KATILIMCIYI DEĞİL —
 * salondaki herkes AYNI kareyi okutur. Katılımcı listesi boşken de üretilir.
 * ✏️ K16 (tur 5): modaldan çıktı — kendi bölümü (P1: modal yalnız onay).
 */
function QrBolumu({ id, ad }: { id: string; ad: string }): React.JSX.Element {
  const uret = useEgitimQrUret();
  return (
    <Bolum baslik="Değerlendirme QR'ı" testId="egitim-qr-bolumu">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Salondaki herkes aynı kareyi okutur; yanıtlar <strong>anonimdir</strong> — kimin
          doldurduğu bilinmez, kim doldurmadı takip edilemez.
        </Text>
        {uret.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="egitim-qr-hata">
            {uret.error.message}
          </Text>
        )}
        <Group>
          <Button
            data-testid="egitim-qr-uret"
            size="xs"
            loading={uret.isPending}
            onClick={() => uret.mutate({ id })}
          >
            Üret
          </Button>
        </Group>
        {uret.isSuccess && <QrSonucu link={uret.data.link} ad={ad} />}
      </Stack>
    </Bolum>
  );
}

function QrSonucu({ link, ad }: { link: string; ad: string }): React.JSX.Element {
  const qr = qrcode(0, 'M');
  qr.addData(link);
  qr.make();
  const svg = qr.createSvgTag({ cellSize: 4, margin: 2 });

  const pngIndir = (): void => {
    // SVG → canvas → PNG (istemcide; sunucuya görüntü çizdirilmez — S4 deseni).
    const img = new Image();
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `egitim-degerlendirme-qr-${ad.replaceAll(' ', '-')}.png`;
        a.click();
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  return (
    <Stack gap="xs" align="flex-start">
      <div data-testid="egitim-qr-karesi" dangerouslySetInnerHTML={{ __html: svg }} />
      <Text size="xs" c="dimmed" data-testid="egitim-qr-link" style={{ wordBreak: 'break-all' }}>
        {link}
      </Text>
      <Button size="xs" variant="light" data-testid="egitim-qr-png" onClick={pngIndir}>
        PNG indir
      </Button>
    </Stack>
  );
}
